import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import DataService from "features/Application/services/dataServices/typedDataService";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { type DistributedOpUpdateParams } from "interfaces";
import { formatFilters } from "utils/filterMapUtils";
import { escapeTerm } from "utils/searchUtils";
import { pluralize } from "utils/stringUtils";
import { filterTypes, type ScormOverview } from "../../types/models";
import { fetchBegin, fetchSuccess, updateScormBegin, updateScormSuccess, fetchFailure } from "../slices/OverviewSlice";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export const fetchAction =
  (skip: number, top: number, sortBy?: string, sortOrder?: string, append?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBegin());
      const filtersState = getState().library.scorm.filters;
      const searchTerm = escapeTerm(filtersState.search);
      const filterQueryParams = formatFilters(filtersState.appliedFilter, filterTypes);
      const result = await DataService.content.getContent({
        top,
        skip,
        sortBy,
        sortOrder,
        filters: filterQueryParams,
        term: searchTerm,
        showPurchased: true,
        contentType: "scorm",
      });

      const items = result.data.map((item) => ({ ...item, thumbnailUrl: item.bag?.thumbnailUrl }));
      const totalCount = Number.parseInt(result.headers[countHeaderName]);
      dispatch(
        fetchSuccess({
          items,
          totalCount,
          append,
        }),
      );
    } catch {
      dispatch(
        fetchSuccess({
          items: [],
          totalCount: 0,
        }),
      );
    }
  };

export const deleteAction =
  (ids: number[], refetch: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.scorm.deleteItem(ids);
      return data;
    };

    const idsCount = ids.length;
    const entity = pluralize("SCORM Package", idsCount);
    const updateParams: DistributedOpUpdateParams = {
      id: "DeleteSCORM",
      title: `Deletion of ${entity}`,
      onCompleted: () => refetch(),
      getOperationProps,
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been deleted!`,
      failureTransientMessage: `${entity} delete failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const changeVisibilityAction =
  (ids: number[], hide: boolean, onCompleted: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const idsCount = ids.length;
    const entity = pluralize("SCORM Package", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "VisibilitySCORMDistributedOperation",
      title: `${hide ? "Showing" : "Hiding"} ${entity}`,
      onCompleted,
      getOperationProps: async () => {
        const { data } = await DataService.scorm.changeVisibility(ids, hide);
        return data;
      },
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been ${hide ? "shown" : "hidden"} successfully!`,
      failureTransientMessage: `${entity} ${hide ? "showing" : "hiding"} failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const updateScormAction = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(updateScormBegin());
  try {
    const result = await DataService.scorm.getScorm(id);
    dispatch(updateScormSuccess(result.data as unknown as ScormOverview));
  } catch (error) {
    dispatch(fetchFailure(error));
  }
};
